import { ChangeEvent, forwardRef, ReactNode, useMemo } from "react";

import MissingDataBanner from "components/MissingDataBanner";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import SegmentedControl from "ds/components/SegmentedControl";
import Select from "ds/components/Select";
import TooltipInfo from "ds/components/TooltipInfo";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import { TrackAnalyticsEventProperties } from "shared/Analytics";
import Card from "ds/components/Card";

import { StackCreationVersion, StackCreationVersionType } from "../types";
import styles from "./styles.module.css";

type VersionInputProps = {
  type: StackCreationVersionType;
  version: string;
  supportedVersions?: string[];
  onChange(version: StackCreationVersion): void;
  errorMessage?: string;
  title: string;
  tooltipInfo: ReactNode;
  reloadVersionsData: () => void;
  reloadLoading: boolean;
  analyticsProps: TrackAnalyticsEventProperties;
};

const VersionInput = forwardRef(function VersionInput(
  {
    title,
    type,
    version,
    supportedVersions,
    onChange,
    errorMessage,
    tooltipInfo,
    reloadVersionsData,
    reloadLoading,
    analyticsProps,
  }: VersionInputProps,
  externalRef: React.Ref<HTMLInputElement>
) {
  const defaultPreciseVersion =
    supportedVersions && supportedVersions.length ? supportedVersions[0] : "";
  const supportedVersionOptions = useMemo(
    () =>
      supportedVersions?.map((value) => ({
        value,
        label: value,
      })),
    [supportedVersions]
  );

  const handleTypeChange = (type: StackCreationVersionType) => {
    if (type === StackCreationVersionType.Specific) {
      onChange({ value: defaultPreciseVersion, type });
    } else if (type === StackCreationVersionType.Range) {
      onChange({ value: version, type });
    }
  };

  const handleVersionChange = (value: string) => onChange({ value, type });

  const handleVersionRangeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const range = event.target.value;
    onChange({
      value: range,
      type: StackCreationVersionType.Range,
    });
  };

  return (
    <Card
      variant="secondary"
      titleTag="p"
      title={title}
      titleSuffix={
        <TooltipInfo
          analyticsPage={AnalyticsPageStack.StackNew}
          analyticsProps={analyticsProps}
          analyticsTitle="Tooltip click"
          variant="modal"
        >
          {tooltipInfo}
        </TooltipInfo>
      }
    >
      {supportedVersionOptions === undefined && (
        <MissingDataBanner refreshLoading={reloadLoading} refreshHandler={reloadVersionsData} />
      )}

      {supportedVersionOptions !== undefined && (
        <>
          <SegmentedControl
            value={type}
            onChange={handleTypeChange}
            options={[
              { value: StackCreationVersionType.Specific, label: "Specific" },
              { value: StackCreationVersionType.Range, label: "Range" },
            ]}
          />
          <FormField
            error={errorMessage}
            helperText={type === StackCreationVersionType.Range && "Use SemVer format"}
          >
            {({ ariaInputProps }) => (
              <>
                {type === StackCreationVersionType.Specific && (
                  <Select
                    value={version}
                    items={supportedVersionOptions}
                    onChange={handleVersionChange}
                    isError={!!errorMessage}
                    {...ariaInputProps}
                  />
                )}
                {type === StackCreationVersionType.Range && (
                  <Input
                    error={!!errorMessage}
                    autoFocus
                    className={styles.input}
                    type="text"
                    value={version || ""}
                    placeholder="Set range"
                    onChange={handleVersionRangeChange}
                    {...ariaInputProps}
                    ref={externalRef}
                  />
                )}
              </>
            )}
          </FormField>
        </>
      )}
    </Card>
  );
});

export default VersionInput;
